import React, { createContext, useContext, useMemo, ReactNode } from 'react';

import { ProductCardTextAlignmentOption } from 'types/graphql';

import {
  ProductCardProduct,
  ProductCardImage,
  ProductCardBadges,
  ProductCardPricing,
  ProductCardLayout,
  CollectionBadgeViewData,
} from './types';

type CardContextValue = {
  productName: string;
  brandName: string;
  href: string;
  strainType: string | null;
  potency: string[];
  image: ProductCardImage;
  isStaffPick: boolean;
  isSponsored: boolean;
  isSpecialOffer: boolean;
  specialNames: string[];
  hideSalesLanguage: boolean;
  collectionBadge: CollectionBadgeViewData | null;
  displayPrice: string;
  standardPrice: string | null;
  discount: string | null;
  showWeight: boolean;
  weight: string;
  buttonCopy: string;
  listPriceEnabled: boolean;
  hideStrikethrough: boolean;
  formatStandardPrice: (price: string) => string;
  isEmbeddedCarousel: boolean;
  fullWidthImages: boolean;
  textAlignment: ProductCardTextAlignmentOption;
  hideSponsoredTag: boolean;
  onLinkClick: () => void;
  onAddToCart: () => void;
};

const CardContext = createContext<CardContextValue | null>(null);

export function useCardContext(): CardContextValue {
  const ctx = useContext(CardContext);
  if (!ctx) {
    throw new Error('useCardContext must be used within a CardProvider');
  }
  return ctx;
}

type CardProviderProps = {
  children: ReactNode;
  product: ProductCardProduct;
  image: ProductCardImage;
  badges: ProductCardBadges;
  pricing: ProductCardPricing;
  layout: ProductCardLayout;
  hideSponsoredTag: boolean;
  onLinkClick: () => void;
  onAddToCart: () => void;
};

/** Distributes pre-computed product card data to sub-components via context. */
export function CardProvider({
  children,
  product,
  image,
  badges,
  pricing,
  layout,
  hideSponsoredTag,
  onLinkClick,
  onAddToCart,
}: CardProviderProps): JSX.Element {
  const value = useMemo<CardContextValue>(
    () => ({
      productName: product.name,
      brandName: product.brandName,
      href: product.href,
      strainType: product.strainType,
      potency: product.potency,
      image,
      ...badges,
      ...pricing,
      ...layout,
      hideSponsoredTag,
      onLinkClick,
      onAddToCart,
    }),
    [product, image, badges, pricing, layout, hideSponsoredTag, onLinkClick, onAddToCart]
  );

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>;
}
